import { Table, Form, type TableProps } from 'antd';
import type { ColumnFilterItem } from 'antd/lib/table/interface';
import { useFormikContext } from 'formik';
import { uniq } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactJson from 'react-json-view';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import { type ListAvailablePermissionsQuery } from '../../../api/queries/listAvailablePermissions';
import TableField from '../../../components/fields/TableField';
import { type FormValues } from './RoleAddPermissionDrawer';
import RoleTags, { tagRegex } from './RoleTags';

type Record = ListAvailablePermissionsQuery['permissions'][number];

type EnhancedColumnFilterItem = ColumnFilterItem & { _key: string };

export type RoleAddPermissionFormProps = {
    role: RoleSpecsFragment;
    loading: boolean;
    permissions: Array<Record>;
    onSubmit: () => void;
    setIsFormValid: React.Dispatch<React.SetStateAction<boolean>>;
};

const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderTags = value => <RoleTags tags={value} />;

const expandable: TableProps<Record>['expandable'] = {
    expandedRowRender: record => (
        <ReactJson
            displayDataTypes={false}
            displayObjectSize={false}
            enableClipboard={false}
            name={null}
            quotesOnKeys={false}
            src={record.raw}
        />
    ),
    rowExpandable: () => true,
};

const RoleAddPermissionForm = ({
    role,
    permissions,
    loading,
    onSubmit,
    setIsFormValid,
}: RoleAddPermissionFormProps) => {
    const { t } = useTranslation(['roleDetails']);

    const { isValid } = useFormikContext<FormValues>();

    useEffect(() => {
        setIsFormValid(isValid);
    }, [isValid, setIsFormValid]);

    const tagFilters = useMemo(() => {
        const tags = uniq(permissions.flatMap(permission => permission.tags)).toSorted();

        return tags.reduce<EnhancedColumnFilterItem[]>((acc, tag) => {
            const { key, value } = tag.match(tagRegex).groups;
            const filterGroup = acc.find(item => item._key === key);
            const label = t(`roleDetails:permissionDrawer.tags.${key}`);
            const item: ColumnFilterItem = { value: `^${escapeRegex(tag)}$`, text: value };

            if (filterGroup) {
                filterGroup.children.push(item);

                return acc;
            }

            return [
                ...acc,
                {
                    _key: key,
                    text: label,
                    value: `^${key}::.+$`,
                    children: [item],
                },
            ];
        }, []);
    }, [permissions, t]);

    return (
        <Form id="addPermissions" layout="vertical" name="addPermissions" onSubmitCapture={onSubmit}>
            <TableField
                dataSource={permissions}
                expandable={expandable}
                loading={loading && !permissions.length}
                name="permissions"
                rowKey="id"
                required
            >
                <Table.Column<Record>
                    dataIndex="displayName"
                    onFilter={(value: string, record) => new RegExp(value, 'i').test(record.displayName)}
                    sorter={(a, b) => a.displayName.localeCompare(b.displayName)}
                    title={t('roleDetails:permissionDrawer.columns.displayName')}
                    filterSearch
                />
                <Table.Column<Record>
                    dataIndex="tags"
                    filterMode="tree"
                    filters={tagFilters}
                    onFilter={(value: string, record) => {
                        const regExp = new RegExp(value);

                        return record.tags.some(tag => regExp.test(tag));
                    }}
                    render={renderTags}
                    title={t('roleDetails:permissionDrawer.columns.tags')}
                />
                <Table.Column dataIndex="description" title={t('roleDetails:permissionDrawer.columns.description')} />
            </TableField>
        </Form>
    );
};

export default RoleAddPermissionForm;
